import { useActiveParticipantsViewCreator } from '@viewCreators/active-participants-view-creator';
import React, { useEffect, useState } from 'react';
import { AccountabilityPartnerStore } from '@stores/AccountabilityPartnerStore';
import { useCohortAccountabilityPartnersViewCreator } from '@app/view-creator/CohortAccountabilityPartnersViewCreator';
import { RawAccountabilityPartner } from '@app/views/RawAccountabilityPartner';
import { ParticipantView } from '@app/views';

export type CurrentAccountabilityPartnersWithNames = {
  id: number;
  participantId: number;
  participantName: string;
  partnerId: number;
  partnerName: string;
};

type AccountabilityPartnerPairingEditorController = {
  isLoading: boolean;
  currentAccountabilityPartnersWithNames: CurrentAccountabilityPartnersWithNames[];
  rows: CurrentAccountabilityPartnersWithNames[];
  participants: ParticipantView[];
  deleteAllPartners: () => Promise<void>;
  deletePartnerRecordById: (partnershipId: number) => Promise<void>;
  handleAddRow: () => void;
  handleCancelNewRow: () => void;
  handleSaveNewRow: () => Promise<void>;
  updateNewRowParticipant: (participantId: number) => void;
  updateNewRowPartner: (partnerId: number) => void;
};
export const useAccountabilityPartnerPairingEditorController = (
  groupId: number
): AccountabilityPartnerPairingEditorController => {
  const accountabilityPartnerStore = new AccountabilityPartnerStore();
  const {
    data: activeParticipants,
    isLoading,
    refetch: refetchActiveParticipants,
  } = useActiveParticipantsViewCreator(groupId);
  const {
    data: currentPartnerships,
    isLoading: isLoadingCurrentPartners,
    refetch: refetchCurrentPartnerships,
  } = useCohortAccountabilityPartnersViewCreator(groupId);
  const [currentAccountabilityPartnersWithNames, setCurrentAccountabilityPartnersWithNames] = React.useState<
    CurrentAccountabilityPartnersWithNames[]
  >([]);
  const [rows, setRows] = useState<CurrentAccountabilityPartnersWithNames[]>([]);
  const participants = activeParticipants ?? [];

  const reloadData = () => {
    refetchActiveParticipants();
    refetchCurrentPartnerships();
  };

  const createPartner = async (partner: RawAccountabilityPartner) => {
    await accountabilityPartnerStore.createPartner(partner);
    reloadData();
  };

  const deleteAllPartners = async () => {
    // Confirm the user really wants to delete all partners
    if (!window.confirm('Are you sure you want to delete all accountability partners? This action cannot be undone.')) {
      return;
    }
    await accountabilityPartnerStore.deletePartnersByCohort(groupId);
    reloadData();
  };

  const deletePartnerRecordById = async (partnershipId: number) => {
    // Confirm the user really wants to delete the partner
    if (!window.confirm('Are you sure you want to delete this accountability partner? This action cannot be undone.')) {
      return;
    }
    await accountabilityPartnerStore.deletePartnerRecordById(partnershipId);
    reloadData();
  };

  useEffect(() => {
    setRows(currentAccountabilityPartnersWithNames);
  }, [currentAccountabilityPartnersWithNames]);

  const handleAddRow = () => {
    // Prevent multiple temporary rows (until saved or cancelled)
    if (rows.find((r) => r.id === 0)) {
      window.alert('Please save or cancel the current row before adding another');
      return;
    }

    if (!activeParticipants || activeParticipants.length === 0) {
      window.alert('No active participants available to add as partners.');
      return;
    }

    // Default to first two participants if available
    const defaultParticipantId = activeParticipants[0]?.id ?? 0;
    const secondParticipantId = activeParticipants[1]?.id ?? defaultParticipantId;

    const newRow: CurrentAccountabilityPartnersWithNames = {
      id: 0,
      participantId: defaultParticipantId,
      participantName: activeParticipants.find((p) => p.id === defaultParticipantId)?.name ?? '',
      partnerId: secondParticipantId,
      partnerName: activeParticipants.find((p) => p.id === secondParticipantId)?.name ?? '',
    };

    setRows((prev) => [...prev, newRow]);
  };

  const handleCancelNewRow = () => {
    setRows((prev) => prev.filter((r) => r.id !== 0));
  };

  const handleSaveNewRow = async () => {
    const newRow = rows.find((r) => r.id === 0);
    if (!newRow) return;

    await createPartner({
      cohortId: groupId,
      cohortUserId: newRow.participantId,
      partnerCohortUserId: newRow.partnerId,
    });

    // Remove temporary row locally, we'll refetch and repopulate real rows
    setRows((prev) => prev.filter((r) => r.id !== 0));
  };

  const updateNewRowParticipant = (participantId: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === 0
          ? { ...r, participantId, participantName: participants.find((p) => p.id === participantId)?.name ?? '' }
          : r
      )
    );
  };
  const updateNewRowPartner = (partnerId: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === 0 ? { ...r, partnerId, partnerName: participants.find((p) => p.id === partnerId)?.name ?? '' } : r
      )
    );
  };

  useEffect(() => {
    if (activeParticipants && currentPartnerships) {
      const partnershipsWithNames: CurrentAccountabilityPartnersWithNames[] = currentPartnerships.map((partnership) => {
        const participant = activeParticipants.find((p) => p.id === partnership.cohortUserId);
        const partner = activeParticipants.find((p) => p.id === partnership.partnerCohortUserId);
        return {
          id: partnership.id,
          participantId: partnership.cohortUserId,
          participantName: participant ? participant.name : 'Unknown',
          partnerId: partnership.partnerCohortUserId,
          partnerName: partner ? partner.name : 'Unknown',
        };
      });
      setCurrentAccountabilityPartnersWithNames(partnershipsWithNames);
    }
  }, [activeParticipants, currentPartnerships]);

  return {
    currentAccountabilityPartnersWithNames,
    isLoading,
    rows,
    participants,
    deleteAllPartners,
    deletePartnerRecordById,
    handleAddRow,
    handleCancelNewRow,
    handleSaveNewRow,
    updateNewRowParticipant,
    updateNewRowPartner,
  } as AccountabilityPartnerPairingEditorController;
};
